<template>
  <div>
    <DefaultCard class="z-20 select-none">
      <Heading class="mb-16" center>Account License</Heading>

      <section v-if="license">
        <p class="text-gray-600 uppercase text-sm">Your Selected License</p>
        <p class="font-semibold text-xl mt-2">
          {{ license.name }}
          <span v-if="license.price > 0"> Monthly ${{ license.price }}</span>
          <span v-if="isShared"> (Shared)</span>
        </p>
        <p v-if="license.status == 'active'" class="mt-2 text-sm">
          Next Invoice on: {{ license.next_payment_date }}
        </p>
        <p v-else-if="license.status == 'canceled'" class="mt-2 text-sm">
          Status: CANCELED
        </p>

        <section v-if="!isShared">
          <p class="text-gray-600 uppercase text-sm mt-8">Seats</p>
          <p class="mt-2 text-sm">
            <span class="font-semibold">{{ license.seats_count }}</span> active
            seats
          </p>

          <div
            v-if="license.name !== 'Free'"
            class="flex justify-between flex-wrap"
          >
            <div>
              <button
                @click="increaseSeats()"
                class="mt-2 text-sm font-semibold cursor-pointer hover:underline text-primary inline-block"
              >
                + Add Seats</button
              ><br />
              <button
                @click="decreaseSeats()"
                class="text-sm font-semibold cursor-pointer hover:underline text-primary inline-block"
              >
                - Remove Seats</button
              ><br />
              <span
                v-if="license.seats_count > 1"
                @click="manageTeamMemberModal = true"
                class="mt-4 text-sm cursor-pointer inline-block underline"
                >Invite Team Members</span
              >
            </div>

            <!-- confirmation -->
            <div v-if="licenseSeatsUpdatedCount != 0" class="bg-gray-100 p-3">
              <p>
                Are you sure that you want to
                {{ licenseSeatsUpdatedCount > 0 ? "add" : "remove" }}
                {{ licenseSeatsUpdatedCountAbs }} seat{{
                  licenseSeatsUpdatedCount > 1 ? "s" : ""
                }}?
              </p>
              <p v-if="licenseSeatsUpdatedCount > 0" class="text-center">
                You will be charged for
                <span class="font-semibold"
                  >${{ license.price * licenseSeatsUpdatedCountAbs }}</span
                >
              </p>
              <div class="text-center mt-4">
                <span
                  @click="confirmUpdateSeats"
                  class="font-semibold text-sm p-2 bg-primary rounded-md text-white cursor-pointer transform hover:scale-95 transition-transform duration-100 inline-block"
                >
                  Confirm
                </span>
                <span
                  class="text-sm p-2 cursor-pointer"
                  @click="licenseSeatsUpdatedCount = 0"
                  >Cancel</span
                >
              </div>
            </div>
            <!-- ### -->
          </div>
        </section>

        <section v-if="!isShared">
          <p class="text-gray-600 uppercase text-sm mt-8">Total Price</p>
          <p class="mt-2 text-sm">
            <span class="font-semibold"
              >${{ license.price * license.seats_count }}</span
            >
            per month
          </p>
          <div class="flex mt-8">
            <StyledButton
              @click="changeLicenseModal = true"
              type="primary"
              class="mr-4"
              >Upgrade or Downgrade</StyledButton
            >
            <StyledButton
              v-if="license.status == 'canceled'"
              @click="activateLicense()"
              >Activate</StyledButton
            >
            <StyledButton
              v-else-if="license.status == 'active' && license.name !== 'Free'"
              @click="cancelLicense()"
              >Cancel</StyledButton
            >
          </div>
        </section>
      </section>

      <section v-else>
        <p>No license. Press button below to add license.</p>
        <StyledButton
          @click="changeLicenseModal = true"
          type="primary"
          class="mt-4"
          >Choose License</StyledButton
        >
      </section>
    </DefaultCard>

    <LicenseChangeModal
      v-if="changeLicenseModal"
      @close="changeLicenseModal = false"
    />
    <ManageTeamMembersModal
      :licenseid="license.id"
      :seats="license.seats_count"
      v-if="manageTeamMemberModal && license"
      @close="manageTeamMemberModal = false"
      @seatRemoved="getLicense()"
    />
  </div>
</template>

<script>
import DefaultCard from "../layout/DefaultCard";
import Heading from "../Heading";
import LoadingButton from "../LoadingButton";
import StyledButton from "@/components/common/controls/StyledButton";
import LicenseChangeModal from "@/components/modals/account/LicenseChangeModal";
import ManageTeamMembersModal from "@/components/modals/account/ManageTeamMembersModal";
import { mapActions } from "vuex";
import ApiV1 from "@/api/api_v1.0";

export default {
  name: "AccountLicense",
  components: {
    LoadingButton,
    Heading,
    DefaultCard,
    StyledButton,
    LicenseChangeModal,
    ManageTeamMembersModal
  },
  data() {
    return {
      loading: false,
      license: null,
      changeLicenseModal: false,
      manageTeamMemberModal: false,

      licenseSeatsUpdatedCount: 0
    };
  },

  created() {
    this.$root.$on("closeLicenseModal", this.getLicense);
    this.getLicense();
  },

  beforeDestroy() {
    this.$root.$off("closeLicenseModal", this.getLicense);
  },

  methods: {
    ...mapActions(["setSuccessMessage", "setErrorMessage"]),

    async getLicense() {
      const response = await ApiV1.getLicense();

      this.license = response.data;
    },
    async cancelLicense() {
      await ApiV1.cancelLicense();
      this.getLicense();
    },

    async activateLicense() {
      await ApiV1.activateLicense();
      this.getLicense();
    },

    increaseSeats() {
      this.licenseSeatsUpdatedCount++;
    },
    decreaseSeats() {
      this.licenseSeatsUpdatedCount--;
    },
    async confirmUpdateSeats() {
      const action =
        this.licenseSeatsUpdatedCount > 0 ? "increase" : "decrease";

      await ApiV1.updateSeats(this.license.id, {
        action: action,
        count: this.licenseSeatsUpdatedCountAbs
      });

      this.licenseSeatsUpdatedCount = 0;

      this.getLicense();
    }
  },

  computed: {
    licenseSeatsUpdatedCountAbs() {
      return Math.abs(this.licenseSeatsUpdatedCount);
    },
    isShared() {
      return this.license.shared;
    }
  }
};
</script>
